import { useEffect, useState } from "react";
import { Button, TextArea } from "../../components/BaseComponents";
import useApi from "../../hooks/useApi";
import { getCSRFToken } from "../../lib/utils";
import AdminEmailTemplates from "./AdminEmailTemplates";
import AccordionSection from "../../components/BaseComponents/AccordionSection";

type SettingField = 'termsConditions' | 'defaultDisclosureText' | 'defaultMethodologyText';

type SystemTextCardProps = {
    id: string;
    title: string;
    field: SettingField;
    initialValue: string;
}

// Each card saves only its own text; the server keeps the other texts from the current version.
function SystemTextCard({ id, title, field, initialValue }: SystemTextCardProps) {
    const api = useApi();
    const [value, setValue] = useState(initialValue);
    const [savedValue, setSavedValue] = useState(initialValue);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [failed, setFailed] = useState(false);

    const onSave = async () => {
        setBusy(true);
        setMessage('');
        setFailed(false);
        try {
            await api.put('admin/settings', {
                _csrf: await getCSRFToken(),
                settings: { [field]: value },
            });
            setSavedValue(value);
            setMessage('Saved.');
        } catch (_) {
            setFailed(true);
            setMessage('Could not save. Please try again.');
        }
        setBusy(false);
    }

    return (
        <AccordionSection id={id} title={title}>
            <TextArea
                aria-label={title}
                disabled={busy}
                className="mb-4"
                value={value}
                onChange={e => setValue(e.target.value)}
            />
            {message && (<p role={failed ? 'alert' : 'status'} className={`mb-4 ${failed ? 'text-error-200' : ''}`}>{message}</p>)}
            <Button type="Primary" text="Save" size="sm" disabled={busy || value === savedValue} onClick={onSave}/>
        </AccordionSection>
    );
}

function AdminTextSettings() {
    const [settings, setSettings] = useState<any>(null);
    const [failed, setFailed] = useState(false);

    const api = useApi();

    useEffect(() => {
        const run = async () => {
            try {
                const result = await api.get('admin/settings');
                setSettings(result.data);
            } catch (_) {
                setFailed(true);
            }
        }
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div>
            <h3 className="text-white">
                System
            </h3>
            <p className="mt-2 text-sm text-white">
                Every message Neoni displays to users can be edited here. Changes apply immediately; earlier versions are kept.
            </p>
            {failed && (<p role="alert" className="mt-4 text-error-200">System texts could not be loaded.</p>)}
            {!settings && !failed && (<p className="mt-4 text-white">Loading system texts...</p>)}
            {settings && (
                <>
                    <SystemTextCard id="system-terms-conditions" title="General: Terms & Conditions" field="termsConditions" initialValue={settings.terms_conditions ?? ''} />
                    <SystemTextCard id="system-confirmation-statement" title="Report: Confirmation Statement" field="defaultDisclosureText" initialValue={settings.default_disclosure_text ?? ''} />
                    <SystemTextCard id="system-methodology" title="Report: Methodology" field="defaultMethodologyText" initialValue={settings.default_methodology_text ?? ''} />
                </>
            )}
            <AdminEmailTemplates />
        </div>
    )
}

export default AdminTextSettings;
